import { flowRoleColor } from './flowColors'
import type { FlowFocus, FlowHighlight } from './flowFocus'
import type { LaidOutFlowNode } from './flowLayout'

const HIT_PADDING = 6
const DIM_OPACITY = 0.15

function nodeOpacity(node: LaidOutFlowNode, highlight: FlowHighlight | null) {
  if (!highlight) return 1
  return highlight.nodes.has(node.name) ? 1 : DIM_OPACITY
}

// Each bar gets a padded, focusable hit area, so thin bars are easy to
// point at and keyboard users can step through them.
export function FlowNodes({
  nodes,
  highlight,
  onFocus,
}: {
  nodes: LaidOutFlowNode[]
  highlight: FlowHighlight | null
  onFocus: (focus: FlowFocus) => void
}) {
  return (
    <g>
      {nodes.map((node) => {
        const x = node.x0 ?? 0
        const y = node.y0 ?? 0
        const width = (node.x1 ?? 0) - x
        const height = (node.y1 ?? 0) - y
        const focusThis = () => onFocus({ type: 'node', name: node.name })
        return (
          <g key={node.name}>
            <rect
              x={x}
              y={y}
              width={width}
              height={height}
              rx={1}
              fill={flowRoleColor(node.role)}
              opacity={nodeOpacity(node, highlight)}
              className="transition-opacity duration-150"
            />
            {/* biome-ignore lint/a11y/useSemanticElements: an svg bar has no button element */}
            <rect
              x={x - HIT_PADDING}
              y={y - HIT_PADDING}
              width={width + HIT_PADDING * 2}
              height={height + HIT_PADDING * 2}
              fill="transparent"
              role="button"
              tabIndex={0}
              aria-label={node.label}
              className="cursor-pointer outline-none"
              onPointerEnter={focusThis}
              onPointerLeave={() => onFocus(null)}
              onFocus={focusThis}
              onBlur={() => onFocus(null)}
            />
          </g>
        )
      })}
    </g>
  )
}
